import { AnyAction, Dispatch } from 'redux'
import { camelize, snakeize } from 'casing'
import { AxiosError, AxiosResponse } from 'axios'
import { get, isEmpty, snakeCase } from 'lodash'
import {
  API,
  actionTypeWrapper,
  cloudAccountServiceUrl,
  engineServiceUrl,
  errorLogger,
  getErrorMessages,
  LoadingTypes,
  RequestFailureMessage,
  startLoading,
  stopLoading,
  updateAppAlert,
  type AlertMessageType,
  type CloudType as CommonCloudType,
  type ProductType,
} from '@labrav/common'
import type { TestButtonStatus } from '@labrav/react-components/lib/@types/components/TestButtonComponent'
import type { InputsFields } from '@labrav/react-components/lib/@types/components/CFTemplateView'

import { patchSellersData } from '../../api/marketplace/index'
import { FlyOutSettingsSuccessMessage } from '../../common/utils/messagesContants'
import { CloudType } from '../../types/index'
import { FlyOutListingUserType } from '../../types/external'

const temporaryArn =
  'arn:aws:iam::123456789012:role/dummy-partnerId-for-managed'

const SET_ACCOUNTS = 'SET_ACCOUNTS'
const UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS =
  'UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS'
const SET_TEMPLATE_INPUT_FIELD = 'SET_TEMPLATE_INPUT_FIELD'
const SET_TEST_STATUS_ARN = 'SET_TEST_STATUS_ARN'

export interface TemplateRows {
  id: string
  inputsFields: InputsFields[]
  [key: string]: unknown
}

export interface AWSAccountDetails {
  externalAccountId?: string
  name?: string
  origin?: FlyOutListingUserType
  isIsv?: boolean
  roles: {
    rows: TemplateRows[]
  }
  partnershipTier?: string
  partnershipTypes?: string
  industries?: string
  terms?: boolean
  isSrrp?: boolean
  accelerateProgram?: boolean
}

export type AccountRegistrationRootState = {
  loading: unknown
  accounts: Partial<Record<string, Array<{ id?: string }>>>
  accountForm: Record<
    string,
    {
      cloudType: string
      accountDetails: {
        AWS: AWSAccountDetails
      }
    }
  >
  AccountData?: {
    user?: {
      partnerData?: {
        partnerId?: string
      }
    }
  }
}

export const updateValueOnAccountDetailsForAWS = (
  key: string,
  value: unknown
) =>
  ({
    type: UPDATE_VALUE_ON_ACCOUNT_DETAILS_AWS,
    payload: {
      key,
      value,
    },
  }) as unknown as AnyAction

export const setTemplateInputFields = (
  templateId: string,
  keyName: string,
  value: string
) => ({
  type: SET_TEMPLATE_INPUT_FIELD,
  payload: {
    templateId,
    keyName,
    value,
  },
})

const setStatus = (
  templateId: string,
  status: TestButtonStatus,
  keyName: string,
  error?: string
) => ({
  type: SET_TEST_STATUS_ARN,
  payload: {
    templateId,
    status,
    keyName,
    error,
  },
})

const setAccounts = (accounts: Array<{ id?: string }>) => ({
  type: SET_ACCOUNTS,
  payload: accounts,
})

const testTemplateEndpoint = (
  templateId: string,
  cloudType: CommonCloudType,
  iamRoleArn: string,
  partnerId: string
) =>
  API.get(`${cloudAccountServiceUrl}/templates/${templateId}/verifications`, {
    params: {
      cloud_type: cloudType,
      iam_role_arn: iamRoleArn,
      partner_id: partnerId,
    },
  })

const postFlyoutAccountDetails = (data: Record<string, unknown>) =>
  API.post(`${cloudAccountServiceUrl}/accounts`, data)

const patchAccount = (accountId: string, data: unknown) =>
  API.patch(`${cloudAccountServiceUrl}/accounts/${accountId}`, data)

const patchPartnerOldEngineService = (
  partnerId: string,
  data: Record<string, unknown>
) => API.patch(`${engineServiceUrl}/partners/${partnerId}`, data)

const fetchOrganizationAccounts = (
  organizationId: string,
  cloudType: string,
  productId?: string
) =>
  API.get(`${cloudAccountServiceUrl}/accounts`, {
    params: {
      cloud_type: cloudType === 'AZURE' ? 'AZ' : cloudType,
      organization_id: organizationId,
      product_id: productId || '',
    },
  })

export const testTemplate =
  (
    templateId: string,
    cloudType: CommonCloudType,
    productType: ProductType,
    partnerId: string,
    keyName: string
  ) =>
  async (dispatch: Dispatch, getState: () => AccountRegistrationRootState) => {
    await dispatch(
      actionTypeWrapper(productType, setStatus(templateId, 'loading', keyName))
    )

    const state = getState()
    const templateInputsFields = state.accountForm[
      productType
    ].accountDetails.AWS.roles.rows.find(r => r.id === templateId)?.inputsFields
    const iamRoleArn =
      templateInputsFields?.find(
        templateInputsField => templateInputsField.keyName === keyName
      )?.value || ''
    try {
      await testTemplateEndpoint(templateId, cloudType, iamRoleArn, partnerId)
      await dispatch(
        actionTypeWrapper(
          productType,
          setStatus(templateId, 'success', keyName)
        )
      )
    } catch (err) {
      await dispatch(
        actionTypeWrapper(
          productType,
          setStatus(
            templateId,
            'error',
            keyName,
            getErrorMessages([RequestFailureMessage])(
              (err as AxiosError).response as AxiosResponse<ErrorResponse>
            )
          )
        )
      )
      const error: Error = {
        name: 'productType',
        message: `Getting error in this product Type: ${productType} , with this templateId ${templateId}`,
      }
      const globalState = getState()
      errorLogger({ globalState })(error)
    }
  }

export const sendAccountRegistrationData =
  (productType: ProductType, partnerId: string, onboardingComplete?: boolean) =>
  async (dispatch: Dispatch, getState: () => AccountRegistrationRootState) => {
    await dispatch(startLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    try {
      const state = getState()
      const data = state.accountForm[productType]
      const origin = data.accountDetails.AWS.origin
      const isFlyOut = productType === 'flyout'
      const shouldSendNameOrAccountId =
        productType === 'oppsync' ||
        (isFlyOut && origin !== FlyOutListingUserType.MANAGED)
      const name = shouldSendNameOrAccountId ? data.accountDetails.AWS.name : ''
      const externalAccountId = shouldSendNameOrAccountId
        ? data.accountDetails.AWS.externalAccountId
        : ''
      const roles = data.accountDetails.AWS.roles.rows.map(row => {
        const arn = get(row, 'inputsFields[0].value', '')
        return {
          templateId: row.id,
          iamRoleArn: isEmpty(arn) ? temporaryArn : arn,
        }
      })
      const accountId = (state.accounts[productType] || [])[0]?.id
      const newData = {
        ...data,
        accountDetails: {
          name,
          externalAccountId,
          type: 'ROOT',
          roles: { rows: roles },
        },
        name: `${productType}_prd`,
      }
      const isIsv = data.accountDetails.AWS.isIsv
      let dataToUpdatePartner: Record<string, unknown> = {
        isIsv: isIsv,
      }

      if (productType === 'oppsync') {
        dataToUpdatePartner = {
          ...dataToUpdatePartner,
          partnershipTier: data.accountDetails.AWS.partnershipTier,
          partnershipType: snakeCase(data.accountDetails.AWS.partnershipTypes),
          industry: data.accountDetails.AWS.industries,
          isSRRP: data.accountDetails.AWS.isSrrp,
          acceptedTermsAndConditions: data.accountDetails.AWS.terms,
          accelerateProgram: isIsv
            ? data.accountDetails.AWS.accelerateProgram
            : false,
        }
      }

      if (accountId) {
        if (shouldSendNameOrAccountId) {
          await patchAccount(
            accountId,
            snakeize({ accountDetails: newData.accountDetails })
          )
        }
      } else {
        await postFlyoutAccountDetails(snakeize(newData))
      }

      const organizationId =
        state.AccountData?.user?.partnerData?.partnerId || partnerId
      if (organizationId) {
        const response = await fetchOrganizationAccounts(
          organizationId,
          newData.cloudType,
          productType
        )
        await dispatch(
          actionTypeWrapper(
            productType,
            setAccounts(camelize(response.data.rows) as Array<{ id?: string }>)
          )
        )
      }

      await patchPartnerOldEngineService(
        partnerId,
        snakeize(dataToUpdatePartner)
      )
      const alert = { message: '', messageType: '' }
      if (isFlyOut) {
        const sellersMetaData: Record<string, string> = {}
        data.accountDetails.AWS.roles.rows[0]?.inputsFields.forEach(
          field => (sellersMetaData[field.keyName] = field.value)
        )
        const seller = {
          origin: data.accountDetails.AWS.origin,
          awsAccountId: externalAccountId,
          sellersMetaData,
        }
        await patchSellersData(
          partnerId,
          snakeize(seller),
          CloudType.AWS,
          'update_partner_details'
        )
        alert.messageType = 'SUCCESS'
        alert.message = FlyOutSettingsSuccessMessage
      } else {
        alert.messageType =
          productType === accountId && onboardingComplete
            ? 'SUCCESS'
            : 'WARNING'
        alert.message =
          accountId && onboardingComplete
            ? 'Account details updated successfully.'
            : `Your ${newData.cloudType} integration is in progress. Please contact your account manager.`
      }
      await dispatch(
        updateAppAlert({
          message: alert.message,
          messageType: alert.messageType as AlertMessageType,
          autoClose: !!(accountId && onboardingComplete),
        })
      )
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    }
  }
